import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import TeamHoverName from '../components/TeamHoverName';

const FORMAT_LABELS = {
  single_elimination: 'Single Elim',
  double_elimination: 'Double Elim',
  bingo: 'Bingo',
  ffa_scoreboard: 'FFA',
  seeded_single_elimination: 'Seeded Elim',
  time_trial: 'Zeitfahren',
};

const STATUS_CFG = {
  open:        { label: 'Anmeldung offen', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  in_progress: { label: 'Läuft',           cls: 'bg-ocean-50 text-ocean-700 border-ocean-200' },
  finished:    { label: 'Beendet',         cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  cancelled:   { label: 'Abgesagt',        cls: 'bg-red-50 text-red-600 border-red-200' },
};

function MatchStatusDot({ count }) {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-bold bg-amber-100 text-amber-700 border border-amber-300 px-2 py-0.5 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse inline-block" />
      {count} Bestätigung{count > 1 ? 'en' : ''} ausstehend
    </span>
  );
}

export default function MyTournaments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [bingoCardsByEventId, setBingoCardsByEventId] = useState({});
  const [bingoActive, setBingoActive] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    Promise.all([
      api.get('/api/tournaments/my'),
      api.get('/api/bingo/my-cards').catch(() => ({ data: [] })),
      api.get('/api/bingo/settings').catch(() => ({ data: { is_active: false } })),
    ]).then(([tRes, bRes, sRes]) => {
      setTournaments(tRes.data);
      setBingoActive(sRes.data?.is_active ?? false);
      const map = {};
      (bRes.data || []).forEach((card) => {
        if (card.event_id) map[card.event_id] = card;
      });
      setBingoCardsByEventId(map);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="page-title mb-6">Meine Turniere</h1>

      {tournaments.length === 0 ? (
        <div className="card text-center py-14">
          <div className="text-4xl mb-3">🏆</div>
          <p className="font-medium text-slate-600 mb-1">Du bist in keinem Turnier angemeldet.</p>
          <p className="text-sm text-slate-400 mb-5">Besuche ein Event und tritt einem Team bei oder erstelle dein eigenes.</p>
          <Link to="/" className="btn-primary btn-sm no-underline inline-block">Zum aktuellen Event</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {tournaments.map((t) => {
            const cfg = STATUS_CFG[t.status] || STATUS_CFG.open;
            const isBingo = t.format === 'bingo';
            const isTimeTrial = t.format === 'time_trial';
            const team = t.my_team;
            const isCreator = t.my_role === 'creator';
            const isPending = t.my_membership_status === 'pending';
            const confirmed = (team?.members || []).filter((m) => m.status === 'confirmed');
            const teamFull = team?.is_full;

            return (
              <div key={t.id} className="card border-l-4 border-l-ocean-400">
                {/* Tournament header */}
                <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Link to={`/tournaments/${t.id}`} className="font-bold text-ocean-900 hover:underline text-lg">
                        {t.name}
                      </Link>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${cfg.cls}`}>
                        {cfg.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                      {t.game && <span className="font-medium text-ocean-600">{t.game}</span>}
                      <span>{FORMAT_LABELS[t.format] || t.format}</span>
                      {t.team_size > 1 && <span>{t.team_size}v{t.team_size}</span>}
                    </div>
                  </div>
                  <MatchStatusDot count={t.pending_matches} />
                </div>

                {/* Bingo info box */}
                {isBingo && (
                  <div className="rounded-lg border bg-ocean-50 border-ocean-200 p-3 mb-3 text-sm text-ocean-800">
                    Du nimmst automatisch teil – deine Bingo-Karte wurde für dich erstellt.
                  </div>
                )}

                {/* Zeitfahren info box */}
                {isTimeTrial && (
                  <div className="rounded-lg border bg-ocean-50 border-ocean-200 p-3 mb-3 text-sm text-ocean-800">
                    Keine Anmeldung nötig – einfach vorbeikommen und mitspielen.
                  </div>
                )}

                {/* Team box */}
                {!isBingo && !isTimeTrial && team && (
                  <div className={`rounded-lg border p-3 mb-3 ${teamFull ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-800"><TeamHoverName team={team} /></span>
                        {isCreator && (
                          <span className="text-[10px] bg-ocean-100 text-ocean-700 px-1.5 py-0.5 rounded font-bold uppercase">Captain</span>
                        )}
                        {isPending && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold uppercase">Anfrage ausstehend</span>
                        )}
                      </div>
                      <span className={`text-xs font-semibold ${teamFull ? 'text-emerald-600' : 'text-slate-500'}`}>
                        {confirmed.length}/{t.team_size} Spieler{teamFull ? ' ✓' : ''}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {confirmed.map((m) => (
                        <Link key={m.id} to={`/users/${m.user_id}`} className="text-xs bg-white border border-slate-200 rounded-full px-2 py-0.5 text-slate-700 hover:bg-ocean-50 hover:text-ocean-700 hover:border-ocean-300 flex items-center gap-1 no-underline transition-colors">
                          {m.role === 'creator' && <span className="text-ocean-400 text-[10px]">★</span>}
                          {m.user?.nickname || m.user?.username}
                        </Link>
                      ))}
                      {Array.from({ length: Math.max(0, t.team_size - confirmed.length) }).map((_, i) => (
                        <span key={i} className="text-xs border border-dashed border-slate-200 rounded-full px-2 py-0.5 text-slate-300">
                          Offen
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <Link to={`/tournaments/${t.id}`} className="no-underline btn-sm btn-secondary">
                    {isBingo || isTimeTrial ? 'Turnierseite' : 'Turnierbaum'}
                  </Link>
                  {isBingo && bingoActive && bingoCardsByEventId[t.event_id] && (
                    <Link to={`/bingo/${bingoCardsByEventId[t.event_id].token}`} className="no-underline btn-sm btn-primary">
                      Zur Bingo-Karte
                    </Link>
                  )}
                  {!isBingo && team && (isCreator || !isPending) && (
                    <Link to={`/teams/${team.id}`} className="no-underline btn-sm btn-primary">
                      {isCreator ? 'Team verwalten' : 'Mein Team'}
                    </Link>
                  )}
                  {t.pending_matches > 0 && (
                    <Link to={`/tournaments/${t.id}`} className="no-underline btn-sm"
                      style={{ background: '#fef3c7', color: '#b45309', border: '1px solid #fcd34d' }}>
                      Ergebnisse bestätigen
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
